import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "@/lib/toast";
import { useStore } from "@/context/StoreContext";
import { useAuth } from "@/context/AuthContext";
import { PRIORIDADES, ORIGENS } from "@/lib/constants";
import { Page, readFileAsDataUrl } from "@/components/ui-z";

// produto_id: produto escolhido na lista da Customização (obrigatório); a API grava o nome dele como categoria
const init = { solicitante_nome: "", solicitante_contato: "", titulo: "", descricao: "", prioridade: "media", origem: "portal", origem_ref: "", produto_id: "" };

export default function NovoChamado() {
  const { createTicket, produtos } = useStore();
  const ativos = produtos.filter((p) => p.ativo);
  const { user } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState(init);
  const [files, setFiles] = useState([]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const addFiles = async (list) => {
    const arr = await Promise.all([...list].map(readFileAsDataUrl));
    setFiles((p) => [...p, ...arr]);
  };

  const onPaste = (e) => {
    const imgs = [...(e.clipboardData?.files || [])];
    if (imgs.length) addFiles(imgs);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.solicitante_nome || !f.titulo || !f.descricao || !f.produto_id) return toast.error("Preencha solicitante, título, descrição e produto");
    const t = await createTicket({ ...f, produto_id: Number(f.produto_id), evidencias: files }, user);
    if (!t) return;
    toast.success(`Chamado ${t.codigo} aberto`);
    nav(`/chamados/${t.id}`);
  };

  return (
    <Page title="Abertura de Chamado" crumbs={[["Chamados", "/chamados"], ["Novo"]]}>
      <form onSubmit={submit} onPaste={onPaste} data-testid="new-ticket-form">
        <div className="row">
          <div className="col-lg-8">
            <div className="card card-primary">
              <div className="card-header"><h3 className="card-title">Dados do chamado</h3></div>
              <div className="card-body">
                <div className="row">
                  <div className="col-md-6 form-group">
                    <label htmlFor="solicitante">Nome do solicitante *</label>
                    <input id="solicitante" data-testid="input-solicitante" className="form-control" value={f.solicitante_nome} onChange={set("solicitante_nome")} />
                  </div>
                  <div className="col-md-6 form-group">
                    <label htmlFor="contato">Contato (e-mail / telefone)</label>
                    <input id="contato" data-testid="input-contato" className="form-control" value={f.solicitante_contato} onChange={set("solicitante_contato")} />
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="titulo">Título *</label>
                  <input id="titulo" data-testid="input-titulo" className="form-control" value={f.titulo} onChange={set("titulo")} />
                </div>
                <div className="form-group">
                  <label htmlFor="descricao">Descrição do problema *</label>
                  <textarea id="descricao" data-testid="input-descricao" rows={6} className="form-control" value={f.descricao} onChange={set("descricao")} />
                </div>
                <div className="row">
                  <div className="col-md-6 form-group mb-0">
                    <label htmlFor="prioridade">Prioridade</label>
                    <select id="prioridade" data-testid="input-prioridade" className="form-control" value={f.prioridade} onChange={set("prioridade")}>{PRIORIDADES.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}</select>
                  </div>
                  <div className="col-md-6 form-group mb-0">
                    <label htmlFor="produto">Produto *</label>
                    <select id="produto" data-testid="input-produto" className="form-control" value={f.produto_id} onChange={set("produto_id")}>
                      <option value="">{ativos.length ? "Selecione o produto" : "Nenhum produto cadastrado"}</option>
                      {ativos.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-4">
            <div className="card card-secondary">
              <div className="card-header"><h3 className="card-title">Origem / Vínculo</h3></div>
              <div className="card-body">
                <div className="form-group">
                  <label htmlFor="origem">Canal de origem</label>
                  <select id="origem" data-testid="input-origem" className="form-control" value={f.origem} onChange={set("origem")}>{ORIGENS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}</select>
                </div>
                <div className="form-group mb-0">
                  <label htmlFor="origem-ref">ID da conversa de origem</label>
                  <input id="origem-ref" data-testid="input-origem-ref" className="form-control" placeholder="ex.: chat_123 / thread-abc" value={f.origem_ref} onChange={set("origem_ref")} />
                </div>
              </div>
            </div>

            <div className="card card-secondary">
              <div className="card-header"><h3 className="card-title">Evidências</h3></div>
              <div className="card-body">
                <div className="form-group">
                  <div className="custom-file">
                    <input id="evidencias" data-testid="input-evidencias" type="file" multiple className="custom-file-input" onChange={(e) => addFiles(e.target.files)} />
                    <label className="custom-file-label" htmlFor="evidencias">Escolher arquivos</label>
                  </div>
                  <small className="form-text text-muted">Você também pode colar um print (Ctrl+V).</small>
                </div>
                <ul className="list-unstyled mb-0" data-testid="evidence-preview-list">
                  {files.map((fl, i) => (
                    <li key={i} className="d-flex align-items-center mb-2">
                      {fl.content_type.startsWith("image/") ? <img src={fl.data_url} alt="" className="img-thumbnail mr-2" style={{ width: 48, height: 48, objectFit: "cover" }} /> : <i className="fas fa-file fa-2x text-muted mr-2" />}
                      <span className="text-sm text-truncate flex-fill">{fl.name}</span>
                      <button type="button" className="btn btn-tool" data-testid={`remove-evidence-${i}`} onClick={() => setFiles(files.filter((_, j) => j !== i))}><i className="fas fa-times" /></button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <button data-testid="new-ticket-submit-btn" type="submit" className="btn btn-primary btn-block mb-3"><i className="fas fa-paper-plane mr-1" />Abrir chamado</button>
          </div>
        </div>
      </form>
    </Page>
  );
}
